import type { CSSProperties } from "react"
import { useAssembly } from "@/hooks/useAssembly"
import { assemble, decode, overshoot, reveal } from "@/utils/glyphMotion"
import type { GlyphRow } from "@/utils/glyphMotion"

/**
 * The curtain assembling itself — screen 8a, "Scan-lit", from
 * `Report generation & Break screens explorations.dc`.
 *
 * The motif's glyphs fly in from off-screen, the badge, title and body rise
 * behind them, then the three beats of the round arrive one at a time: the
 * number counting to its own digit, the label decoding out of noise, the
 * description settling under it. A blue scan line crosses the row once as they
 * land, which is the only thing tying the three together as one instruction.
 *
 * Every value below is a pure function of one progress number, so the whole
 * screen is a single clock. See `utils/glyphMotion` for why, and `useAssembly`
 * for what happens under `prefers-reduced-motion` (the arc starts finished).
 *
 * Kept out of the component because `RoundTransition` is already the busiest
 * file in the app: it decides who may drive, which of two writes a button makes,
 * and which pains are still on the list. None of that belongs in the same
 * function as an easing curve.
 */

/** How long the curtain takes to draw itself. The reference's `breakDuration`. */
const CURTAIN_MS = 3800

/*
 * Every start time is a fraction of the arc, straight from the doc. They are
 * deliberately not evenly spaced: the drawing gets the first third to itself,
 * the copy rises under it, and the beats are late enough that the room has
 * looked up before the thing it is meant to read appears.
 */
const BADGE_AT = 0.3
const TITLE_AT = 0.34
const BODY_AT = 0.42
const SWEEP_AT = 0.5
const SWEEP_WINDOW = 0.42
const NOTE_AT = 0.52
const STEP_AT = 0.52
/** One beat per step. Three steps therefore finish around 0.84. */
const STEP_GAP = 0.11
/** A curtain that only asks a question has no beats to wait for. */
const CTA_AT = 0.9
const CTA_AT_DECISION = 0.7

/** The motif takes the top 40% of the arc and scatters half again as far. */
const MOTIF_BOX = { top: 0, height: 0.4, spread: 1.5, hues: true }

/*
 * How the motif is set, by how wide it is drawn.
 *
 * The curtains carry two shapes of drawing: the narrow ones — the sticky notes,
 * the job frame, the fanned deck, the fit board, all 13 or 14 characters — and
 * the wide row of four cards at 34. One cell size cannot serve both: the wide
 * one at the narrow metric runs off a projected screen, and the narrow one at
 * the wide metric is a postage stamp. Both pairs are the reference's.
 */
const WIDE_FROM = 20
const NARROW = { cell: "11px", fontSize: "17px", lineHeight: "20px" }
const WIDE = { cell: "10px", fontSize: "15px", lineHeight: "18px" }

/** A rising element: how far it starts below its place, in px. */
function rise(progress: number, at: number, window: number, distance: number): CSSProperties {
  const e = reveal(progress, at, window)
  return {
    opacity: e,
    transform: `translateY(${(distance * (1 - overshoot(e))).toFixed(1)}px)`,
  }
}

export interface CurtainStep {
  /** The digit, or a spinning one while the number is still counting up. */
  num: string
  numStyle: CSSProperties
  /** Mid-count the ring and the digit are faint — it has not settled yet. */
  settled: boolean
  label: string
  labelStyle: CSSProperties
  labelSettled: boolean
  detailStyle: CSSProperties
}

export interface CurtainMotion {
  motif: GlyphRow[]
  /** How the drawing is set: its column count and the metrics that fit it. */
  motifColumns: number
  motifCell: string
  motifFontSize: string
  motifLineHeight: string
  badgeStyle: CSSProperties
  titleStyle: CSSProperties
  bodyStyle: CSSProperties
  /** The scan line's position across the row of beats, and its fade. */
  sweepStyle: CSSProperties
  steps: CurtainStep[]
  /** The gate's one-line note, decoded rather than stepped. */
  note: (text: string) => string
  noteSettled: boolean
  listStyle: CSSProperties
  actionsStyle: CSSProperties
}

interface CurtainMotionInput {
  lines: readonly string[]
  labels: readonly string[]
  /** A curtain that asks (the job gate, the pick screen) rather than announces. */
  decision: boolean
  /** Changes when the curtain does, so the next round assembles from scratch. */
  replayKey: unknown
}

export function useCurtainMotion({
  lines,
  labels,
  decision,
  replayKey,
}: CurtainMotionInput): CurtainMotion {
  /*
   * No motif means no curtain: `RoundTransition` calls this on every render of
   * every session, curtain or not, because a hook cannot be conditional — and
   * it passes an empty drawing in between. There is nothing to animate then,
   * so the clock is told not to start; without that, the idle call ran a full
   * arc of frames re-rendering a component that returns null, on mount and
   * again after every dismissal.
   */
  const p = useAssembly(CURTAIN_MS, replayKey, lines.length > 0)

  const sweep = reveal(p, SWEEP_AT, SWEEP_WINDOW)
  const noteIn = reveal(p, NOTE_AT, 0.16)

  const columns = lines.reduce((widest, line) => Math.max(widest, line.length), 0)
  const metrics = columns > WIDE_FROM ? WIDE : NARROW

  return {
    motif: assemble(p, lines, MOTIF_BOX),
    motifColumns: columns,
    motifCell: metrics.cell,
    motifFontSize: metrics.fontSize,
    motifLineHeight: metrics.lineHeight,

    badgeStyle: rise(p, BADGE_AT, 0.1, 10),
    titleStyle: rise(p, TITLE_AT, 0.12, 16),
    bodyStyle: rise(p, BODY_AT, 0.1, 12),

    /* A band, not a bar: it brightens into the middle of its travel and is gone
       by the far side, so it reads as something passing over the three beats
       rather than as a fourth element on the row. */
    sweepStyle: {
      left: `${(sweep * 100).toFixed(1)}%`,
      opacity: sweep > 0 && sweep < 1 ? Math.sin(sweep * Math.PI) : 0,
    },

    steps: labels.map((label, index) => {
      const at = STEP_AT + index * STEP_GAP
      const counted = reveal(p, at, 0.1)
      const labelIn = reveal(p, at + 0.03, 0.13)
      const counting = counted > 0 && counted < 1

      return {
        /*
         * The number rolls before it lands. Quantised into six ticks across its
         * own window rather than recomputed per frame: a digit changing sixty
         * times a second is a grey smudge, and it has to be a function of `p`
         * alone — reading a clock here would make the render impure.
         */
        num: counting
          ? String(1 + ((Math.floor(counted * 6) + index * 13) % 9))
          : String(index + 1),
        numStyle: {
          opacity: counted,
          transform: `scale(${(0.6 + 0.4 * overshoot(counted)).toFixed(3)})`,
        },
        settled: counted >= 1,
        label: decode(label, labelIn, index + 3),
        /* Full opacity from the first noise character: the label is REVEALED by
           decoding, so fading it in as well would be the same beat twice. */
        labelStyle: { opacity: labelIn > 0 ? 1 : 0 },
        labelSettled: labelIn >= 1,
        detailStyle: rise(p, at + 0.07, 0.12, 8),
      }
    }),

    note: (text: string) => decode(text, noteIn, 31),
    noteSettled: noteIn >= 1,

    /* The list and the buttons come last, together: the facilitator should have
       heard the three beats read out before the screen offers a way past them. */
    listStyle: rise(p, NOTE_AT, 0.14, 10),
    actionsStyle: rise(p, decision ? CTA_AT_DECISION : CTA_AT, 0.1, 10),
  }
}
